// manutenzione-sse.service.ts
import { Injectable } from '@angular/core';
import {
  catchError,
  EMPTY,
  fromEvent,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  throwError,
  timer,
} from 'rxjs';
import { UtenteWebService } from '../utente/utente-web.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UtilityService } from '../utility/utility.service';
import { UtenteService } from '../utente/utente.service';

export interface Maintenance {
  maintenance: boolean;
  message: string;
  start_time: string;
  end_time: string;
  fileCambiato: boolean;
}

/**
 * Service per gestire lo stato di manutenzione dell'applicazione.
 *
 * Questo servizio effettua richieste periodiche al JSON di manutenzione
 * ospitato su Google Cloud Storage e utilizza l'header ETag per ridurre
 * il traffico HTTP e sfruttare la cache locale.
 *
 * Il servizio adatta automaticamente l'URL del JSON in base all'ambiente
 * (dev, test, prod, locale).
 */
@Injectable({ providedIn: 'root' })
export class ManutenzioneSseService {
  private maintenanceStatus$?: Observable<{ data: Maintenance; changed: boolean }>;

  /**
   * URL base per il JSON di manutenzione.
   * Viene completato dinamicamente nel costruttore in base all'ambiente.
   */
  private _baseUrl = 'https://storage.googleapis.com/sostenibile-cloud-notifiche/manutenzioneJson/';

  /** Cache locale dei dati di manutenzione */
  private cachedData: any = null;

  /** ETag dell'ultima risposta HTTP ricevuta */
  private cachedEtag: string | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly utenteWebService: UtenteWebService,
  ) {
    // Seleziona il JSON corretto in base all'ambiente
    switch (location.host) {
      case 'admin.dev.sostenibile.cloud':
      case 'admin.dev.go.sostenibile.cloud':
        this._baseUrl = this._baseUrl + 'manutenzione_dev.json';
        break;
      case 'admin.test.sostenibile.cloud':
      case 'admin.test.go.sostenibile.cloud':
        this._baseUrl = this._baseUrl + 'manutenzione_test.json';
        break;
      case 'adm.sostenibile.cloud':
      case 'adm.go.sostenibile.cloud':
        this._baseUrl =
          'https://storage.googleapis.com/sostenibile-cloud-notifiche-prod/manutenzioneJson/manutenzione_prod.json';
        break;
      case 'localhost:4200':
      case 'localgo:4200':
        this._baseUrl = this._baseUrl + 'manutenzione_local.json';
        break;
      default:
        break;
    }
  }

  /**
   * Esegue il recupero dello stato di manutenzione dal server con caching condizionale (ETag)
   * e polling automatico ogni 15 minuti, solo quando la scheda è visibile.
   *
   * ### Funzionalità principali:
   * - Utilizza un flusso `visibility$` per rilevare quando la scheda del browser è visibile o nascosta (cambio tab).
   *   - Quando la scheda **non è visibile**, il polling viene **sospeso** (nessuna richiesta HTTP).
   *   - Quando la scheda torna **visibile**, il polling riprende automaticamente.
   *
   * - Esegue richieste HTTP GET all’endpoint `_baseUrl` ogni 900.000 ms (15 minuti).
   * - Implementa un sistema di **cache condizionale tramite ETag**:
   *   - Se il server risponde con `304 Not Modified`, viene riutilizzato il valore già in cache (`cachedData`).
   *   - Se viene ricevuto un nuovo ETag, aggiorna sia `cachedEtag` che `cachedData`.
   *
   * - In caso di errore HTTP (es. server offline), se è disponibile un valore in cache,
   *   lo restituisce senza generare un errore.
   *
   * ### Flusso operativo:
   * 1. Subscrive all’evento `visibilitychange` del documento.
   * 2. Quando la tab è visibile → avvia un `timer(0, 900000)` che scatena le richieste periodiche.
   * 3. Ogni richiesta:
   *    - Invia l’intestazione `If-None-Match` con l’ETag precedente (se presente).
   *    - Gestisce le risposte `200 OK` (nuovi dati) e `304 Not Modified` (cache valida).
   * 4. Ritorna un oggetto `{ data, changed }` dove:
   *    - `data`: istanza aggiornata di `Maintenance`.
   *    - `changed`: `true` se i dati sono nuovi, `false` se provengono dalla cache.
   *
   * ### Vantaggi:
   * - Riduce drasticamente il carico di rete.
   * - Aggiorna automaticamente i dati solo quando necessario.
   * - Evita richieste superflue se la scheda è in background.
   *
   * ### Gestione errori:
   * - In caso di errore HTTP e presenza di dati cache → ritorna la cache (`changed: false`).
   * - Se nessun dato cache disponibile → rilancia l’errore.
   *
   * @private
   * @returns {Observable<{ data: Maintenance; changed: boolean }>}
   *   Observable che emette periodicamente lo stato di manutenzione aggiornato.
   */

  private fetchMaintenanceStatus(): Observable<{ data: Maintenance; changed: boolean }> {
    // stream che emette true/false quando cambia la visibilità della tab
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => document.visibilityState === 'visible'),
      startWith(document.visibilityState === 'visible'), // stato iniziale
      shareReplay(1), // così non si ricalcola per ogni subscribe
    );

    return visibility$.pipe(
      switchMap((visible) => {
        if (!visible) {
          // se non visibile → non fare polling (Observable vuoto)
          return EMPTY;
        }

        // se visibile → avvia polling
        return timer(0, 900000).pipe(
          switchMap(() => {
            let headers = new HttpHeaders({ 'Cache-Control': 'no-cache' });

            if (this.cachedEtag) {
              headers = headers.set('If-None-Match', this.cachedEtag);
            }

            return this.http
              .get<Maintenance>(this._baseUrl, {
                headers,
                observe: 'response',
              })
              .pipe(
                switchMap((resp) => {
                  if (resp.status === 304 && this.cachedData) {
                    console.log('304 Not Modified → uso cache');
                    return of({ data: this.cachedData, changed: false });
                  }

                  const etag = resp.headers.get('ETag');
                  this.cachedEtag = etag ?? null;
                  this.cachedData = resp.body!;

                  console.log('Nuovo JSON → aggiornamento cache, etag:', etag);
                  return of({ data: resp.body!, changed: true });
                }),
                catchError((err) => {
                  if (this.cachedData) {
                    return of({ data: this.cachedData, changed: false });
                  }
                  return throwError(() => err);
                }),
              );
          }),
        );
      }),
    );
  }

  /**
   * Restituisce lo stato di manutenzione dell'applicazione.
   *
   * - Effettua la chiamata HTTP solo la prima volta.
   * - Mantiene in memoria l'ultimo valore emesso tramite `shareReplay`.
   * - Utilizza la logica di caching con ETag e polling periodico (ogni 15 minuti).
   *
   * @returns Observable che emette un oggetto contenente:
   *   - `data`: i dati di manutenzione (tipo `Maintenance`)
   *   - `changed`: `true` se i dati sono stati aggiornati dall'ultima richiesta
   */
  public getMaintenanceStatus(): Observable<{ data: Maintenance; changed: boolean }> {
    if (!this.maintenanceStatus$) {
      this.maintenanceStatus$ = this.utenteWebService.checkAuth().pipe(
        switchMap((autorizzato) => {
          if (!autorizzato) {
            return of({ data: null as any, changed: false });
          } else {
            return this.fetchMaintenanceStatus();
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }), // ✅ mantiene l’ultimo valore in memoria
      );
    }

    return this.maintenanceStatus$;
  }

  /**
   * Verifica lo stato di manutenzione *senza autenticazione* e *senza caching*.
   *  * Adesso lo uso solamente per la chiamata nella gaurdia di manutenzione!!!
   * - Esegue una singola chiamata HTTP diretta (no polling, no shareReplay).
   * - Ignora completamente la logica di autenticazione e visibilità del documento.
   * - Ritorna `true` solo se la manutenzione è attiva e l'ora corrente è compresa
   *   tra `start_time` ed `end_time`.
   *
   * @returns Observable<boolean> che emette:
   *   - `true` se il sito è in manutenzione attiva
   *   - `false` altrimenti o in caso di errore HTTP
   */
  public getManutenzioneStatusNoAuth(): Observable<boolean> {
    const headers = new HttpHeaders({ 'Cache-Control': 'no-cache' });

    return this.http.get<Maintenance>(this._baseUrl, { headers }).pipe(
      map((data) => {
        const now = new Date();
        const startTime = new Date(data.start_time);
        const endTime = new Date(data.end_time);

        return data.maintenance === true && now >= startTime && now <= endTime;
      }),
      catchError((err) => {
        console.error('Errore nello stato manutenzione:', err);
        return of(false);
      }),
    );
  }

  public resetMaintenanceStatus() {
    this.maintenanceStatus$ = undefined;
  }
}
